/**
 * VAULT · Public Demo Dashboard (persona-scoped)
 *
 * PURE client-side. Reads deterministic fixtures from `data/demo_fixtures`.
 * Never calls the backend, never sets an auth cookie, works in incognito
 * with zero session state. Tour buttons are anchor scrolls only.
 *
 * Palette follows Midnight Luxe: Deep Midnight Navy shell, Warm Ivory
 * hero surface, Selective Gold rail, vibrant data accents (emerald,
 * cobalt, coral, violet, teal, amber).
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import BrandLogo from "@/components/BrandLogo";
import {
    Sparkles, ShieldCheck, ArrowRight, ArrowUpRight, RefreshCcw,
    CreditCard, Wallet, Users, Home as HomeIcon, User as UserIcon,
    CalendarClock, Repeat, Landmark, FileText, PieChart, TrendingUp,
    Coins, ShieldAlert, Info,
} from "lucide-react";
import {
    DEMO_FIXTURES, TONE, fmtINR, relativeDate,
} from "@/data/demo_fixtures";

const PERSONAS = {
    personal: { icon: UserIcon, label: "Personal VAULT", blurb: "One person. One organised financial life." },
    couple:   { icon: Users,    label: "Couple VAULT",   blurb: "Mine. Partner. Joint. Together." },
    family:   { icon: HomeIcon, label: "Family VAULT",   blurb: "Shared responsibilities. Individual privacy." },
    business: { icon: HomeIcon, label: "Business Owner VAULT", blurb: "One life. Two ledgers. Cleanly separate." },
};

const TOUR_STEPS = {
    personal: [
        { anchor: "brief",             title: "VAULT Brief",       body: "This is what deserves your attention." },
        { anchor: "whats-next",        title: "What's Next",       body: "See upcoming payments, renewals and obligations." },
        { anchor: "financial-position", title: "Financial Position", body: "Know what you own and what you owe." },
        { anchor: "subs",              title: "Subscriptions",     body: "Know what keeps charging you." },
        { anchor: "docs",              title: "Documents",         body: "Important financial documents in one place." },
        { anchor: "analytics",         title: "Analytics",         body: "See the bigger picture." },
    ],
    couple: [
        { anchor: "zone-mine",     title: "Mine",     body: "What is yours, stays yours." },
        { anchor: "zone-partner",  title: "Partner",  body: "Your partner's private items." },
        { anchor: "zone-joint",    title: "Joint",    body: "Bills, subscriptions and assets you share." },
        { anchor: "zone-together", title: "Together", body: "Shared where it matters. Private where it counts." },
    ],
    family: [
        { anchor: "brief",     title: "Household overview", body: "One view of everything that runs the household." },
        { anchor: "members",   title: "Family members",     body: "Roles and responsibilities per person." },
        { anchor: "calendar",  title: "Shared calendar",    body: "Renewals, fees and reviews with clear owners." },
        { anchor: "docs",      title: "Documents",          body: "One household. Clearer responsibilities. Individual control." },
    ],
    business: [
        { anchor: "brief",           title: "Combined brief",   body: "Both worlds at a glance — Personal and Business, side by side." },
        { anchor: "zone-personal",   title: "Personal zone",    body: "Your personal cards, EMIs, policies and household subs." },
        { anchor: "zone-business",   title: "Business zone",    body: "Corporate cards, vendor invoices, cloud bills, GST, insurance." },
        { anchor: "what-next",       title: "Upcoming",         body: "GST filings, EMIs and invoices in one chronological view." },
        { anchor: "docs",            title: "Documents",        body: "Tax + incorporation + insurance — Business and Personal, tagged separately." },
    ],
};

/* ─── Shared header + banner ───────────────────────────────────────── */
function PublicDemoNav() {
    return (
        <header className="border-b border-white/10 bg-[#0b1e3f]/95 backdrop-blur sticky top-0 z-40" data-testid="demo-public-nav">
            <div className="max-w-6xl mx-auto px-6 py-3 flex items-center gap-6">
                <Link to="/" data-testid="demo-nav-home">
                    <BrandLogo variant="light" height={26} />
                </Link>
                <nav className="hidden md:flex items-center gap-4 text-[13px] text-white/70 ml-2">
                    <Link to="/product"  className="hover:text-white">Product</Link>
                    <Link to="/pricing"  className="hover:text-white">Pricing</Link>
                    <Link to="/demo"     className="text-white">Demo</Link>
                    <Link to="/together" className="hover:text-white">Together</Link>
                    <Link to="/trust"    className="hover:text-white">Trust</Link>
                </nav>
                <div className="ml-auto flex items-center gap-2">
                    <Link to="/login"  className="text-[13px] font-semibold text-white/70 hover:text-white hidden sm:inline">Sign in</Link>
                    <Link to="/signup" className="text-[12.5px] font-semibold text-[#0b1e3f] bg-amber-300 rounded-full px-3.5 py-1.5 hover:bg-amber-200" data-testid="demo-nav-signup">
                        Create your VAULT
                    </Link>
                </div>
            </div>
        </header>
    );
}

function DemoBanner({ onReset, persona }) {
    return (
        <div className="bg-[#0b1e3f] text-white/90 border-b border-amber-300/30" data-testid="demo-banner">
            <div className="max-w-6xl mx-auto px-6 py-2.5 flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-widest text-amber-300 bg-amber-300/10 rounded-full px-2 py-0.5 border border-amber-300/30">
                    <Sparkles size={11}/> Live Demo
                </span>
                <span className="text-[12.5px] text-white/80">
                    You&rsquo;re exploring <b className="text-white">{PERSONAS[persona].label}</b> with sample data. Nothing you do here is saved.
                </span>
                <div className="ml-auto flex items-center gap-2">
                    <button onClick={onReset} className="text-[12px] text-white/70 hover:text-white inline-flex items-center gap-1" data-testid="demo-reset">
                        <RefreshCcw size={11}/> Reset demo
                    </button>
                    <Link to="/signup" className="text-[12px] font-semibold text-[#0b1e3f] bg-amber-300 rounded-full px-3 py-1 hover:bg-amber-200" data-testid="demo-create-vault-banner">
                        Create your VAULT <ArrowRight size={11} className="inline"/>
                    </Link>
                </div>
            </div>
        </div>
    );
}

function TourStepper({ steps, current, setCurrent, onFinish }) {
    if (current === null) return null;
    const step = steps[current];
    return (
        <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 max-w-xl w-[calc(100%-2rem)]" role="dialog" data-testid="demo-tour">
            <div className="rounded-2xl bg-[#0b1e3f] text-white shadow-2xl border border-amber-300/30 p-4">
                <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-widest text-amber-300 font-bold">
                    <Sparkles size={11}/> Step {current + 1} of {steps.length}
                </div>
                <div className="font-serif text-lg mt-1">{step.title}</div>
                <div className="text-[12.5px] text-white/80 mt-1.5">{step.body}</div>
                <div className="flex items-center gap-2 mt-3">
                    <button onClick={() => setCurrent(null)} className="text-[12px] text-white/60 hover:text-white" data-testid="demo-tour-close">Skip tour</button>
                    <div className="flex-1"/>
                    {current > 0 && (
                        <button onClick={() => setCurrent(current - 1)} className="text-[12px] text-white/80 border border-white/20 rounded-full px-3 py-1 hover:border-white" data-testid="demo-tour-back">
                            Back
                        </button>
                    )}
                    {current < steps.length - 1 ? (
                        <button
                            onClick={() => {
                                const next = current + 1;
                                setCurrent(next);
                                const el = document.getElementById(steps[next].anchor);
                                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                            }}
                            className="text-[12px] font-semibold text-[#0b1e3f] bg-amber-300 rounded-full px-3 py-1"
                            data-testid="demo-tour-next"
                        >
                            Next <ArrowRight size={11} className="inline"/>
                        </button>
                    ) : (
                        <button onClick={onFinish} className="text-[12px] font-semibold text-[#0b1e3f] bg-amber-300 rounded-full px-3 py-1" data-testid="demo-tour-finish">
                            Ready for your VAULT? <ArrowRight size={11} className="inline"/>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

/* ─── Reusable UI atoms ────────────────────────────────────────────── */
const Section = ({ id, eyebrow, title, sub, children }) => (
    <section id={id} className="max-w-6xl mx-auto px-6 pt-10" data-testid={`demo-section-${id}`}>
        <div className="text-[10.5px] uppercase tracking-widest text-amber-700 font-bold">{eyebrow}</div>
        <h2 className="font-serif text-2xl text-stone-900 mt-1.5">{title}</h2>
        {sub && <p className="text-[13px] text-stone-500 mt-1 max-w-2xl leading-relaxed">{sub}</p>}
        <div className="mt-5">{children}</div>
    </section>
);

const StatCard = ({ tone, label, value, note }) => (
    <div className="rounded-xl bg-white border border-stone-200 p-4">
        <div className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold rounded-full px-2 py-0.5 border ${TONE[tone]?.chip || TONE.cobalt.chip}`}>
            {label}
        </div>
        <div className="text-2xl font-semibold text-stone-900 mt-2 font-serif">{value}</div>
        <div className="text-[11px] text-stone-500 mt-1">{note}</div>
    </div>
);

const CategoryBar = ({ label, value, max, tone }) => {
    const pct = max ? Math.max(4, Math.round((value / max) * 100)) : 0;
    return (
        <div className="mb-2">
            <div className="flex items-center justify-between text-[12px] mb-1">
                <span className="text-stone-800 font-medium">{label}</span>
                <span className="text-stone-600">{fmtINR(value)}</span>
            </div>
            <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                <div className={`h-full ${TONE[tone]?.bar || "bg-stone-500"}`} style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
};

/* ─── Persona-specific views ───────────────────────────────────────── */
function PersonalView({ data }) {
    const catMax = useMemo(() => Math.max(...data.expenses_by_category.map((c) => c.amount)), [data]);
    const assetTotal = data.financial_position.assets_total;
    const liabTotal = data.financial_position.liabilities_total;
    return (
        <>
            {/* Brief */}
            <Section id="brief" eyebrow="Now · VAULT Brief" title={`Hello ${data.owner.name.split(" ")[0]}, this deserves your attention today.`} sub={data.brief.headline}>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {data.brief.highlights.map((h) => <StatCard key={h.label} {...h} />)}
                </div>
            </Section>

            {/* What's Next */}
            <Section id="whats-next" eyebrow="Next · What's coming" title="Upcoming obligations" sub="Payments, subscriptions and renewals — chronological, always visible.">
                <div className="rounded-2xl border border-stone-200 bg-white divide-y divide-stone-100" data-testid="demo-whats-next">
                    {data.whats_next.map((o, i) => {
                        const tone = TONE[o.tone] || TONE.cobalt;
                        return (
                            <div key={i} className="flex items-center gap-3 px-4 py-3">
                                <div className={`w-9 h-9 rounded-lg ${tone.chip} border flex items-center justify-center`}>
                                    <CalendarClock size={14}/>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-[13.5px] font-medium text-stone-900 truncate">{o.label}</div>
                                    <div className="text-[11.5px] text-stone-500">{relativeDate(o.date)} · {o.kind}</div>
                                </div>
                                <div className="text-right">
                                    <div className="text-[13.5px] font-semibold text-stone-900">{fmtINR(o.amount)}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </Section>

            {/* Financial Position */}
            <Section id="financial-position" eyebrow="Position · What you own vs owe" title={`Net worth: ${fmtINR(data.financial_position.net_worth)}`} sub={`${fmtINR(assetTotal)} assets minus ${fmtINR(liabTotal)} liabilities.`}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="rounded-2xl border border-stone-200 bg-white p-5">
                        <div className="text-[10.5px] uppercase tracking-widest text-emerald-700 font-bold mb-3">Assets · {fmtINR(assetTotal)}</div>
                        {data.financial_position.breakdown_assets.map((a) => (
                            <CategoryBar key={a.key} label={a.label} value={a.value} max={assetTotal} tone={a.tone} />
                        ))}
                    </div>
                    <div className="rounded-2xl border border-stone-200 bg-white p-5">
                        <div className="text-[10.5px] uppercase tracking-widest text-amber-700 font-bold mb-3">Liabilities · {fmtINR(liabTotal)}</div>
                        {data.financial_position.breakdown_liabilities.map((l) => (
                            <CategoryBar key={l.key} label={l.label} value={l.value} max={liabTotal} tone={l.tone} />
                        ))}
                    </div>
                </div>
            </Section>

            {/* Cards */}
            <Section id="cards" eyebrow="Money · Cards" title="Your credit cards" sub="Card numbers are masked — VAULT never displays a full card number.">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="demo-cards">
                    {data.cards.map((c) => (
                        <div key={c.id} className="rounded-2xl bg-gradient-to-br from-[#0b1e3f] to-[#152a52] text-white p-4 border border-white/10">
                            <div className="flex items-center gap-2">
                                <CreditCard size={14} className="text-amber-300"/>
                                <div className="text-[11px] uppercase tracking-widest text-amber-300 font-semibold">{c.bank}</div>
                            </div>
                            <div className="font-serif text-lg mt-2">{c.name}</div>
                            <div className="text-[13px] mt-3 tracking-[0.24em] text-white/80">•••• {c.last_4}</div>
                            <div className="mt-4 grid grid-cols-2 gap-2 text-[11px]">
                                <div>
                                    <div className="text-white/50">Outstanding</div>
                                    <div className="text-white font-semibold">{fmtINR(c.outstanding)}</div>
                                </div>
                                <div>
                                    <div className="text-white/50">Due</div>
                                    <div className="text-white font-semibold">{relativeDate(c.due_day)}</div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </Section>

            {/* Subscriptions */}
            <Section id="subs" eyebrow="Recurring · Subscriptions" title="What keeps charging you" sub="Every recurring charge, linked to the card that pays for it.">
                <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden" data-testid="demo-subs">
                    <table className="w-full text-[13px]">
                        <thead className="bg-stone-50 border-b border-stone-100">
                            <tr className="text-left text-[10.5px] uppercase tracking-widest text-stone-500">
                                <th className="px-4 py-2.5">Subscription</th>
                                <th className="px-4 py-2.5">Amount</th>
                                <th className="px-4 py-2.5">Frequency</th>
                                <th className="px-4 py-2.5">Next</th>
                                <th className="px-4 py-2.5">Card</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-100">
                            {data.subscriptions.map((s) => (
                                <tr key={s.id}>
                                    <td className="px-4 py-3 text-stone-900 font-medium">{s.name}</td>
                                    <td className="px-4 py-3 text-stone-800">{fmtINR(s.amount)}</td>
                                    <td className="px-4 py-3 text-stone-600">{s.freq}</td>
                                    <td className="px-4 py-3 text-stone-600">{relativeDate(s.next)}</td>
                                    <td className="px-4 py-3 text-stone-600">•••• {s.card_last_4}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Section>

            {/* Analytics */}
            <Section id="analytics" eyebrow="Analytics · Expenses" title="Where your money went this month" sub="Categorised, colour-coded, at-a-glance.">
                <div className="rounded-2xl border border-stone-200 bg-white p-5">
                    {data.expenses_by_category.map((c) => (
                        <CategoryBar key={c.category} label={c.category} value={c.amount} max={catMax} tone={c.tone} />
                    ))}
                </div>
            </Section>

            {/* Investments */}
            <Section id="investments" eyebrow="Investments" title="Portfolio snapshot" sub="Read-only overview. VAULT never gives investment advice.">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {data.investments.map((i) => (
                        <div key={i.name} className="rounded-xl bg-white border border-stone-200 p-4">
                            <div className={`inline-flex text-[10px] font-bold uppercase tracking-widest rounded-full px-2 py-0.5 border ${TONE[i.tone].chip}`}>{i.name}</div>
                            <div className="mt-3 font-serif text-xl text-stone-900">{fmtINR(i.value)}</div>
                        </div>
                    ))}
                </div>
            </Section>

            {/* Documents */}
            <Section id="docs" eyebrow="Vault · Documents" title="Important paperwork, organised" sub="Insurance, warranty, property, tax — one place, with renewal cues.">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="demo-docs">
                    {data.documents.map((d) => (
                        <div key={d.id} className="rounded-xl border border-stone-200 bg-white p-4 flex items-start gap-3">
                            <div className="w-9 h-9 rounded-lg bg-violet-50 text-violet-700 border border-violet-200 flex items-center justify-center">
                                <FileText size={14}/>
                            </div>
                            <div className="flex-1">
                                <div className="text-[13.5px] font-medium text-stone-900">{d.name}</div>
                                <div className="text-[11.5px] text-stone-500 mt-0.5">{d.type} · Owner: {d.owner}</div>
                                {d.renewal && (
                                    <div className="mt-1.5 text-[11px] font-semibold inline-flex items-center gap-1 text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                                        Renew {relativeDate(d.renewal)}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </Section>
        </>
    );
}

function CoupleView({ data }) {
    return (
        <>
            <Section id="brief" eyebrow="Household brief" title={`${data.owner.name} & ${data.owner.partner}`} sub={data.brief.headline}>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {data.brief.highlights.map((h) => <StatCard key={h.label} {...h} />)}
                </div>
            </Section>

            {data.zones.map((z) => (
                <Section
                    key={z.key}
                    id={`zone-${z.key}`}
                    eyebrow={`Zone · ${z.label}`}
                    title={z.label === "Mine" ? `${z.owner}'s private items`
                         : z.label === "Partner" ? `${z.owner}'s private items`
                         : z.label === "Joint" ? "Shared between both of you"
                         : "Together — what you both authorised for shared visibility"}
                    sub={z.label === "Together"
                        ? "Nothing marked Mine or Partner leaks into Together — the privacy line is the product."
                        : `${z.items.length} item${z.items.length === 1 ? "" : "s"}.`}
                >
                    <div className="rounded-2xl border border-stone-200 bg-white divide-y divide-stone-100" data-testid={`demo-zone-${z.key}`}>
                        {z.items.map((it, idx) => (
                            <div key={idx} className="flex items-center gap-3 px-4 py-3">
                                <div className={`w-9 h-9 rounded-lg border flex items-center justify-center ${
                                    z.key === "mine"     ? TONE.cobalt.chip :
                                    z.key === "partner"  ? TONE.violet.chip :
                                    z.key === "joint"    ? TONE.emerald.chip :
                                                            TONE.amber.chip
                                }`}>
                                    {it.kind === "card"     ? <CreditCard size={14}/>   :
                                     it.kind === "sub"      ? <Repeat size={14}/>       :
                                     it.kind === "loan"     ? <Landmark size={14}/>     :
                                     it.kind === "bill"     ? <Wallet size={14}/>       :
                                     it.kind === "policy"   ? <ShieldCheck size={14}/>  :
                                     it.kind === "asset"    ? <HomeIcon size={14}/>     :
                                     it.kind === "docs"     ? <FileText size={14}/>     :
                                     it.kind === "calendar" ? <CalendarClock size={14}/> :
                                                              <Info size={14}/>}
                                </div>
                                <div className="flex-1">
                                    <div className="text-[13.5px] font-medium text-stone-900">{it.name}</div>
                                    <div className="text-[11.5px] text-stone-500">{z.owner === "Household" ? "Household view" : `Owner: ${z.owner}`}</div>
                                </div>
                                {it.amount > 0 && (
                                    <div className="text-[13.5px] font-semibold text-stone-900">{fmtINR(it.amount)}</div>
                                )}
                            </div>
                        ))}
                    </div>
                    {z.key !== "together" && (
                        <div className="mt-2 text-[11.5px] text-stone-500 flex items-center gap-1.5">
                            <ShieldCheck size={11} className="text-emerald-700"/>
                            Only <b>{z.owner === "Both" ? "shared" : z.owner}</b>-scoped items appear here.
                        </div>
                    )}
                </Section>
            ))}

            <Section id="what-next" eyebrow="Household · What's next" title="Upcoming shared obligations" sub="Scope tag makes it obvious who owns what.">
                <div className="rounded-2xl border border-stone-200 bg-white divide-y divide-stone-100">
                    {data.whats_next.map((o, i) => {
                        const tone = TONE[o.tone] || TONE.cobalt;
                        return (
                            <div key={i} className="flex items-center gap-3 px-4 py-3">
                                <div className={`w-9 h-9 rounded-lg ${tone.chip} border flex items-center justify-center`}>
                                    <CalendarClock size={14}/>
                                </div>
                                <div className="flex-1">
                                    <div className="text-[13.5px] font-medium text-stone-900">{o.label}</div>
                                    <div className="text-[11.5px] text-stone-500">{relativeDate(o.date)} · {o.kind} · <b>{o.scope}</b></div>
                                </div>
                                <div className="text-[13.5px] font-semibold text-stone-900">{fmtINR(o.amount)}</div>
                            </div>
                        );
                    })}
                </div>
            </Section>
        </>
    );
}

function FamilyView({ data }) {
    return (
        <>
            <Section id="brief" eyebrow="Household · Family" title={`The ${data.owner.name.split(" ")[1]} household`} sub={data.brief.headline}>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {data.brief.highlights.map((h) => <StatCard key={h.label} {...h} />)}
                </div>
            </Section>

            <Section id="members" eyebrow="People · Family members" title="Who's on this VAULT" sub="Every member has a role and a set of responsibilities.">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="demo-members">
                    {data.members.map((m) => (
                        <div key={m.name} className="rounded-xl bg-white border border-stone-200 p-4 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-[#0b1e3f] text-amber-300 flex items-center justify-center font-serif">
                                {m.name.charAt(0)}
                            </div>
                            <div className="flex-1">
                                <div className="text-[13.5px] font-medium text-stone-900">{m.name}</div>
                                <div className="text-[11.5px] text-stone-500">{m.role} · {m.email}</div>
                            </div>
                            <div className={`text-[10.5px] font-bold uppercase tracking-widest rounded-full px-2 py-0.5 border ${TONE.violet.chip}`}>
                                {m.responsibilities} task{m.responsibilities === 1 ? "" : "s"}
                            </div>
                        </div>
                    ))}
                </div>
            </Section>

            <Section id="calendar" eyebrow="Shared calendar" title="Everything that's coming up" sub="Clear owner per event. No ambiguity.">
                <div className="rounded-2xl border border-stone-200 bg-white divide-y divide-stone-100" data-testid="demo-family-calendar">
                    {data.shared_calendar.map((e, i) => {
                        const tone = TONE[e.tone] || TONE.cobalt;
                        return (
                            <div key={i} className="flex items-center gap-3 px-4 py-3">
                                <div className={`w-9 h-9 rounded-lg ${tone.chip} border flex items-center justify-center`}>
                                    <CalendarClock size={14}/>
                                </div>
                                <div className="flex-1">
                                    <div className="text-[13.5px] font-medium text-stone-900">{e.label}</div>
                                    <div className="text-[11.5px] text-stone-500">{relativeDate(e.date)} · Owner: <b>{e.owner}</b></div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </Section>

            <Section id="responsibilities" eyebrow="Responsibilities" title="Who owns what" sub="One household. Clearer responsibilities. Individual control.">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {data.responsibilities.map((r) => (
                        <div key={r.task} className="rounded-xl border border-stone-200 bg-white p-4">
                            <div className="text-[13.5px] font-medium text-stone-900">{r.task}</div>
                            <div className="text-[11.5px] text-stone-500 mt-1">Owner: <b>{r.owner}</b></div>
                            <div className={`mt-2 inline-flex text-[11px] font-semibold rounded-full px-2 py-0.5 border ${TONE.amber.chip}`}>{r.status}</div>
                        </div>
                    ))}
                </div>
            </Section>

            <Section id="subs" eyebrow="Family · Subscriptions" title="Recurring household charges" sub="Includes education subs (BYJU'S) and streaming plans.">
                <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
                    <table className="w-full text-[13px]">
                        <thead className="bg-stone-50 border-b border-stone-100">
                            <tr className="text-left text-[10.5px] uppercase tracking-widest text-stone-500">
                                <th className="px-4 py-2.5">Subscription</th>
                                <th className="px-4 py-2.5">Amount</th>
                                <th className="px-4 py-2.5">Frequency</th>
                                <th className="px-4 py-2.5">Owner</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-100">
                            {data.subscriptions.map((s) => (
                                <tr key={s.name}>
                                    <td className="px-4 py-3 text-stone-900 font-medium">{s.name}</td>
                                    <td className="px-4 py-3 text-stone-800">{fmtINR(s.amount)}</td>
                                    <td className="px-4 py-3 text-stone-600">{s.freq}</td>
                                    <td className="px-4 py-3 text-stone-600">{s.owner}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Section>

            <Section id="docs" eyebrow="Household · Documents" title="Shared paperwork" sub="Everyone knows where the important papers live.">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="demo-family-docs">
                    {data.documents.map((d) => (
                        <div key={d.name} className="rounded-xl border border-stone-200 bg-white p-4 flex items-start gap-3">
                            <div className="w-9 h-9 rounded-lg bg-violet-50 text-violet-700 border border-violet-200 flex items-center justify-center">
                                <FileText size={14}/>
                            </div>
                            <div className="flex-1">
                                <div className="text-[13.5px] font-medium text-stone-900">{d.name}</div>
                                <div className="text-[11.5px] text-stone-500 mt-0.5">{d.type} · Owner: {d.owner}</div>
                                {d.renewal && (
                                    <div className="mt-1.5 text-[11px] font-semibold inline-flex items-center gap-1 text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                                        Renew {relativeDate(d.renewal)}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </Section>
        </>
    );
}

/* ─── Main page ────────────────────────────────────────────────────── */
// Deep-link `?view=` → in-page anchor. Keeps invalid values as no-op so
// /demo/personal?view=invalid falls back to the persona home cleanly.
const VIEW_TO_ANCHOR = {
    cards:         "zone-personal",
    subscriptions: "zone-personal",
    documents:     "zone-personal",
    together:      "zone-together",
    joint:         "zone-together",
};

export default function DemoDashboard() {
    const { persona } = useParams();
    const [resetKey, setResetKey] = useState(0);
    const [tourStep, setTourStep] = useState(null);

    // Deep-link handling — read ?view= and scroll the demo to the right zone.
    useEffect(() => {
        if (typeof window === "undefined") return;
        const params = new URLSearchParams(window.location.search);
        const view = params.get("view");
        if (!view) return;
        const anchor = VIEW_TO_ANCHOR[view];
        if (!anchor) return;   // invalid value = silent fallback to persona home
        const t = setTimeout(() => {
            const el = document.getElementById(anchor);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 400);
        return () => clearTimeout(t);
    }, [persona]);

    if (!persona || !DEMO_FIXTURES[persona]) {
        return <Navigate to="/demo" replace />;
    }
    const data = DEMO_FIXTURES[persona];
    const meta = PERSONAS[persona];
    const Icon = meta.icon;
    const steps = TOUR_STEPS[persona] || [];

    const reset = () => setResetKey((k) => k + 1);
    const pageTitle = `${meta.label} \u00b7 Live Demo \u00b7 VAULT by PD`;
    const pageDesc  = `Explore ${meta.label} with completely synthetic financial data. No signup required.`;
    const canonical = `https://vaultbypd.com/demo/${persona}`;

    return (
        <div key={resetKey} className="min-h-screen bg-stone-50 text-stone-900" data-testid={`demo-persona-${persona}`}>
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDesc} />
                <link rel="canonical" href={canonical} />
            </Helmet>

            <PublicDemoNav />
            <DemoBanner onReset={reset} persona={persona} />

            {/* Hero */}
            <section className="border-b border-stone-200 bg-gradient-to-br from-stone-50 via-stone-50 to-amber-50/40">
                <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col md:flex-row items-start gap-6" data-testid="demo-hero">
                    <div className="w-14 h-14 rounded-2xl bg-[#0b1e3f] text-amber-300 flex items-center justify-center">
                        <Icon size={22}/>
                    </div>
                    <div className="flex-1">
                        <div className="text-[10.5px] uppercase tracking-widest text-amber-700 font-bold">Explore VAULT · {meta.label}</div>
                        <h1 className="font-serif text-3xl sm:text-4xl leading-tight mt-1.5">{meta.blurb}</h1>
                        <p className="text-sm text-stone-600 mt-3 max-w-xl leading-relaxed">
                            You&rsquo;re seeing completely synthetic data — {data.owner.name || data.owner.name} and household are fictional.
                            Nothing you do here affects a real account, and no signup is required.
                        </p>
                        <div className="mt-5 flex flex-wrap items-center gap-2">
                            <button
                                onClick={() => {
                                    setTourStep(0);
                                    const first = document.getElementById(steps[0].anchor);
                                    if (first) first.scrollIntoView({ behavior: "smooth", block: "start" });
                                }}
                                className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-white bg-[#0b1e3f] hover:bg-[#152a52] rounded-full px-4 py-2"
                                data-testid="demo-start-tour"
                            >
                                <Sparkles size={12}/> Start guided tour <ArrowRight size={13}/>
                            </button>
                            <Link to="/demo" className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-stone-700 border border-stone-300 rounded-full px-4 py-2 hover:border-stone-500" data-testid="demo-choose-another">
                                Choose another demo
                            </Link>
                            <Link to="/signup" className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#0b1e3f] bg-amber-300 hover:bg-amber-200 rounded-full px-4 py-2 ml-auto" data-testid="demo-create-your-vault">
                                Create your VAULT <ArrowUpRight size={12}/>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Persona view */}
            {persona === "personal" && <PersonalView data={data} />}
            {persona === "couple"   && <CoupleView   data={data} />}
            {persona === "family"   && <FamilyView   data={data} />}
            {persona === "business" && <CoupleView   data={data} />}

            {/* Fictional data disclaimer */}
            <section className="max-w-6xl mx-auto px-6 pt-10 pb-14">
                <div className="rounded-2xl border-l-4 border-amber-400 bg-amber-50/50 p-4 flex items-start gap-2.5" data-testid="demo-disclaimer">
                    <ShieldAlert size={16} className="mt-0.5 text-amber-800 shrink-0"/>
                    <div className="text-[12.5px] text-stone-700 leading-relaxed">
                        All names, balances, transactions, documents and financial information in this demo are <b>fictional</b> and used only to demonstrate VAULT. No real customer information is exposed here.
                    </div>
                </div>
            </section>

            {/* Conversion */}
            <section className="max-w-6xl mx-auto px-6 pb-14" data-testid="demo-conversion">
                <div className="rounded-2xl bg-[#0b1e3f] text-white px-6 py-8 flex flex-wrap items-center justify-between gap-4 border border-amber-300/20">
                    <div>
                        <div className="text-[10.5px] font-bold uppercase tracking-widest text-amber-300">Like what you see?</div>
                        <div className="font-serif text-2xl mt-1">Bring your own financial life into VAULT.</div>
                        <div className="text-[13px] text-white/70 mt-1.5">14-day free trial. No card required.</div>
                    </div>
                    <div className="flex gap-2">
                        <Link to="/signup" className="text-[13px] font-semibold text-[#0b1e3f] bg-amber-300 hover:bg-amber-200 rounded-full px-5 py-2.5" data-testid="demo-cta-signup">
                            Create your VAULT
                        </Link>
                        <Link to="/pricing" className="text-[13px] font-semibold text-white border border-white/30 hover:border-white rounded-full px-5 py-2.5">
                            See pricing
                        </Link>
                    </div>
                </div>
            </section>

            <footer className="border-t border-stone-200 bg-white">
                <div className="max-w-6xl mx-auto px-6 py-6 text-[11px] text-stone-500 flex flex-wrap items-center gap-3">
                    <BrandLogo variant="dark" height={20}/>
                    <span className="ml-2">© {new Date().getFullYear()} VAULT by PD · Financial life, organised.</span>
                    <span className="ml-auto"><Link to="/privacy" className="hover:text-stone-900">Privacy</Link> · <Link to="/terms" className="hover:text-stone-900">Terms</Link> · <Link to="/trust" className="hover:text-stone-900">Trust</Link></span>
                </div>
            </footer>

            <TourStepper
                steps={steps}
                current={tourStep}
                setCurrent={setTourStep}
                onFinish={() => { setTourStep(null); window.location.href = "/signup"; }}
            />
        </div>
    );
}

// Unused helpers imported for potential later expansion (kept to satisfy lint).
export { PieChart, TrendingUp, Coins };
